/**
 * DashboardCard — standard card shell used throughout dashboard pages.
 *
 * Anatomy: white card wrapper → header slot (title + optional right slot) → body slot.
 * Matches design spec: 14px border-radius, 1px border, shadow-card, white bg.
 */
import { cn } from "@/shared/lib/utils";
import type { ReactNode } from "react";

interface DashboardCardProps {
  title?: string;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** When true, the body has no padding (tables/charts manage their own). */
  noPadding?: boolean;
}

export function DashboardCard({
  title,
  headerLeft,
  headerRight,
  children,
  className,
  bodyClassName,
  noPadding = false,
}: DashboardCardProps) {
  const hasHeader = title || headerLeft || headerRight;

  return (
    <div
      className={cn("overflow-hidden rounded-[var(--radius-xl)] flex flex-col h-full", className)}
      style={{ background: "var(--card)", border: "1px solid var(--border-light)", boxShadow: "var(--shadow-card)" }}
    >
      {hasHeader && (
        <div className="flex items-center justify-between gap-2.5 px-5 pt-2.5 pb-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {title && (
              <span
                className="text-[9.5px] font-bold uppercase tracking-[0.14em] whitespace-nowrap leading-none"
                style={{ color: "var(--text-tertiary)" }}
              >
                {title}
              </span>
            )}
            {headerLeft}
          </div>
          {headerRight && <div className="shrink-0">{headerRight}</div>}
        </div>
      )}
      <div className={cn(!noPadding && "p-5", bodyClassName)}>{children}</div>
    </div>
  );
}
